import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaUsers, FaUserCheck, FaUserTimes, FaClock } from "react-icons/fa";
import adminService from "../../services/adminService";
import Footer from "../../components/layout/Footer";
import AdminHeader from "../../components/layout/AdminHeader";

const AdminDashboard = () => {
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    totalSessions: 0,
  });
  const [users, setUsers] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    organization: "",
    status: "",
    period: "all", // all, year, month, date
    startDate: "",
    endDate: "",
  });

  // Check authentication on component mount
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const user = localStorage.getItem('user');
    
    if (!token || !user) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      navigate("/admin/login", { replace: true });
      return;
    }
    
    try {
      const userData = JSON.parse(user);
      if (userData.user_type !== 'admin') {
        navigate("/admin/login", { replace: true });
        return;
      }
    } catch (err) {
      console.error("Error parsing user data:", err);
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const loadDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch users
        const usersResponse = await adminService.getUsers();
        
        if (usersResponse.status === "success") {
          const allUsers = usersResponse.data.results || usersResponse.data || [];
          setUsers(allUsers);

          
          // Apply filters
          let filteredUsers = allUsers;
          
          // Organization filter
          if (filters.organization) {
            filteredUsers = filteredUsers.filter(u => 
              u.organization?.toLowerCase().includes(filters.organization.toLowerCase())
            );
          }
          
          // Status filter
          if (filters.status === "active") {
            filteredUsers = filteredUsers.filter(u => u.is_active);
          } else if (filters.status === "inactive") {
            filteredUsers = filteredUsers.filter(u => !u.is_active);
          }
          
          // Time period filter
          if (filters.period === "year") {
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
            filteredUsers = filteredUsers.filter(u => new Date(u.date_joined) >= oneYearAgo);
          } else if (filters.period === "month") {
            const oneMonthAgo = new Date();
            oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
            filteredUsers = filteredUsers.filter(u => new Date(u.date_joined) >= oneMonthAgo);
          } else if (filters.period === "custom" && filters.startDate && filters.endDate) {
            const startDate = new Date(filters.startDate);
            const endDate = new Date(filters.endDate);
            endDate.setHours(23, 59, 59, 999); // Include the entire end date
            filteredUsers = filteredUsers.filter(u => {
              const joinDate = new Date(u.date_joined);
              return joinDate >= startDate && joinDate <= endDate;
            });
          }
          
          setStats({
            totalUsers: filteredUsers.length,
            activeUsers: filteredUsers.filter(u => u.is_active).length,
            inactiveUsers: filteredUsers.filter(u => !u.is_active).length,
            totalSessions: filteredUsers.length * 5, // Mock session data
          });
          
          // Calculate activity data for chart (last 6 months)
          const monthlyData = calculateMonthlyActivity(allUsers);
          setActivityData(monthlyData);
        } else {
          console.error("API returned non-success status:", usersResponse);
        }
      } catch (err) {
        console.error("Failed to load dashboard data:", err);
        console.error("Error details:", err.message);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [filters]);

  const calculateMonthlyActivity = (users) => {
    const months = [];
    const now = new Date();
    
    // Get last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'short' });
      const year = date.getFullYear();
      
      // Count users joined in this month
      const usersInMonth = users.filter(user => {
        const joinDate = new Date(user.date_joined);
        return joinDate.getMonth() === date.getMonth() && 
               joinDate.getFullYear() === date.getFullYear();
      });
      
      months.push({
        label: `${monthName} ${year}`,
        shortLabel: monthName,
        count: usersInMonth.length,
        active: usersInMonth.filter(u => u.is_active).length,
      });
    }
    
    return months;
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const panelClass =
    "rounded-xl border border-slate-700/50 bg-slate-800/50 p-4 shadow-xl backdrop-blur-sm sm:p-6";
  const labelClass = "text-sm font-medium text-slate-300";
  const inputClass =
    "w-full rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2.5 text-sm text-slate-200 outline-none transition focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/20";
  const navLinkClass =
    "text-sm font-medium text-blue-500 transition-colors hover:text-blue-400";
  const maxActivityCount = Math.max(...activityData.map((month) => month.count), 1);
  const getActivityBarHeightClass = (count) => {
    const height = (count / maxActivityCount) * 150 + 30;
    const snappedHeight = Math.max(30, Math.min(180, Math.round(height / 5) * 5));
    return `h-[${snappedHeight}px]`;
  };

  return (
    <>
    <div className="min-h-screen bg-[#111318] text-slate-200">
      <AdminHeader
        title="Admin Dashboard"
        subtitle="Limbus Intelligence Management"
      />

      <div className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 lg:px-10 lg:py-10">
        {/* Filters */}
        <div className={`${panelClass} mb-6`}>
          <h3 className="mb-4 text-lg font-semibold text-slate-50">Filters</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Organization</label>
              <input
                type="text"
                name="organization"
                placeholder="Search by organization"
                className={inputClass}
                value={filters.organization}
                onChange={handleFilterChange}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Status</label>
              <select
                name="status"
                className={inputClass}
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">All Users</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Time Period</label>
              <select
                name="period"
                className={inputClass}
                value={filters.period}
                onChange={handleFilterChange}
              >
                <option value="all">All Time</option>
                <option value="year">This Year</option>
                <option value="month">This Month</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>
            
            {filters.period === "custom" && (
              <>
                <div className="flex flex-col gap-2">
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="startDate"
                    className={inputClass}
                    value={filters.startDate}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="endDate"
                    className={inputClass}
                    value={filters.endDate}
                    onChange={handleFilterChange}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        {loading ? (
          <div className="py-16 text-center text-base text-slate-500">
            Loading dashboard data...
          </div>
        ) : (
          <>
            <div className="mb-6 grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
              <div className="flex items-center gap-4 rounded-xl border border-slate-700/50 bg-slate-800/50 p-5 shadow-xl backdrop-blur-sm">
                <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-blue-500/10">
                  <FaUsers className="text-[32px] text-blue-500" />
                </div>
                <div className="flex-1">
                  <h4 className="mb-2 text-sm text-slate-400">Total Users</h4>
                  <p className="m-0 text-3xl font-bold text-slate-50">{stats.totalUsers}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 rounded-xl border border-slate-700/50 bg-slate-800/50 p-5 shadow-xl backdrop-blur-sm">
                <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-emerald-500/10">
                  <FaUserCheck className="text-[32px] text-emerald-500" />
                </div>
                <div className="flex-1">
                  <h4 className="mb-2 text-sm text-slate-400">Active Users</h4>
                  <p className="m-0 text-3xl font-bold text-slate-50">{stats.activeUsers}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 rounded-xl border border-slate-700/50 bg-slate-800/50 p-5 shadow-xl backdrop-blur-sm">
                <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-red-500/10">
                  <FaUserTimes className="text-[32px] text-red-500" />
                </div>
                <div className="flex-1">
                  <h4 className="mb-2 text-sm text-slate-400">Inactive Users</h4>
                  <p className="m-0 text-3xl font-bold text-slate-50">{stats.inactiveUsers}</p>
                </div>
              </div>

              <div className="flex items-center gap-4 rounded-xl border border-slate-700/50 bg-slate-800/50 p-5 shadow-xl backdrop-blur-sm">
                <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-violet-500/10">
                  <FaClock className="text-[32px] text-violet-500" />
                </div>
                <div className="flex-1">
                  <h4 className="mb-2 text-sm text-slate-400">Total Sessions</h4>
                  <p className="m-0 text-3xl font-bold text-slate-50">{stats.totalSessions}</p>
                </div>
              </div>
            </div>

            {/* Activity Chart */}
            <div className={`${panelClass} mb-6`}>
              <h3 className="mb-4 text-lg font-semibold text-slate-50">
                User Registration Timeline (Last 6 Months)
              </h3>
              <div className="py-5">
                <div className="mb-5 overflow-x-auto">
                  <div className="flex h-[200px] min-w-[420px] items-end justify-around gap-2">
                  {activityData.length > 0 ? (
                    activityData.map((month, i) => {
                      const barHeightClass = getActivityBarHeightClass(month.count);
                      return (
                        <div key={i} className="flex flex-1 flex-col items-center gap-2">
                          <div
                            className={`w-full max-w-[60px] rounded-t bg-blue-500 transition-all duration-300 ${barHeightClass}`}
                            title={`${month.label}: ${month.count} new users`}
                          />
                          <span className="text-xs text-slate-500">{month.shortLabel}</span>
                          <span className="mt-1 text-[13px] font-semibold text-blue-500">
                            {month.count}
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <div className="w-full p-10 text-center text-sm text-slate-500">
                      No activity data available
                    </div>
                  )}
                  </div>
                </div>
                <div className="flex flex-wrap justify-center gap-6">
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <div className="h-4 w-4 rounded bg-blue-500" />
                    <span>New User Registrations</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Users Table */}
            <div className={panelClass}>
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-lg font-semibold text-slate-50">Recent Users</h3>
                <Link to="/admin/users" className={navLinkClass}>
                  View All Users →
                </Link>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse">
                  <thead>
                    <tr>
                      <th className="border-b border-slate-700/30 px-3 py-3 text-left text-sm font-semibold text-slate-400">
                        Name
                      </th>
                      <th className="border-b border-slate-700/30 px-3 py-3 text-left text-sm font-semibold text-slate-400">
                        Email
                      </th>
                      <th className="border-b border-slate-700/30 px-3 py-3 text-left text-sm font-semibold text-slate-400">
                        Organization
                      </th>
                      <th className="border-b border-slate-700/30 px-3 py-3 text-left text-sm font-semibold text-slate-400">
                        Status
                      </th>
                      <th className="border-b border-slate-700/30 px-3 py-3 text-left text-sm font-semibold text-slate-400">
                        Joined
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.slice(0, 5).map((user) => (
                      <tr
                        key={user.id}
                        className="border-b border-slate-700/10"
                      >
                        <td className="px-3 py-3 text-sm text-slate-200">
                          {user.first_name} {user.last_name}
                        </td>
                        <td className="px-3 py-3 text-sm text-slate-200">{user.email}</td>
                        <td className="px-3 py-3 text-sm text-slate-200">
                          {user.organization || "N/A"}
                        </td>
                        <td className="px-3 py-3 text-sm text-slate-200">
                          <span
                            className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
                              user.is_active
                                ? "bg-emerald-500/10 text-emerald-500"
                                : "bg-red-500/10 text-red-500"
                            }`}
                          >
                            {user.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-sm text-slate-200">
                          {new Date(user.date_joined).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
     <Footer />
     </>
  );
};

export default AdminDashboard;
